import React, { useEffect, useState } from "react";
import { X, FileText, Settings } from "lucide-react";
import classes from "./MaterialIssuedModal.module.css";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL } from "../../Components/API/Api";

const MaterialIssuedModal = ({ isOpen, onClose, refreshList }) => {
  const [materials, setMaterials] = useState([]);
  const [productionOrders, setProductionOrders] = useState([]);
  const [selectedProductionOrder, setSelectedProductionOrder] = useState("");
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchProductionOrders();
      setMaterials([]);
      setSelectedProductionOrder("");
    }
  }, [isOpen]);

  const fetchProductionOrders = async () => {
    try {
      setLoadingOrders(true);
      const token = localStorage.getItem("authToken");

      const res = await axios.get(
        `${BASE_URL}/v1/production_order/planned`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (Array.isArray(res.data?.data)) {
        setProductionOrders(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching production orders:", error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchProductionMaterials = async (productionOrderId) => {
    try {
      setLoadingMaterials(true);
      const token = localStorage.getItem("authToken");

      const res = await axios.get(
        `${BASE_URL}/v1/production_order/materials`,
        {
          params: {
            production_order_id: productionOrderId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (Array.isArray(res.data?.data)) {
        setMaterials(
          res.data.data.map((item) => ({
            rowId: item.id,
            materialId: Number(item.material_id) || Number(item.id),
            material: item.item?.name || "",
            requisitionNo: item.requisition_number || "",
            quantityRequested: Number(item.quantity_required) || 0,
            quantityIssued: item.quantity_issued || "",
            uom: item.unit_of_measurement || "",
            unitCost: item.unit_cost || item.item?.price || "",
            totalValue: item.total_cost || "",
          }))
        );
      } else {
        setMaterials([]);
      }
    } catch (error) {
      console.error("Error fetching production materials:", error);
      setMaterials([]);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProductionOrder) {
      Swal.fire("Warning", "Please select a production order", "warning");
      return;
    }

    for (let m of materials) {
      if (!m.quantityIssued || Number(m.quantityIssued) <= 0) {
        Swal.fire("Warning", "Please enter quantity issued for all items", "warning");
        return;
      }
    }

    const token = localStorage.getItem("authToken");
    const payload = {
      production_order_id: Number(selectedProductionOrder),
      requisition_id: materials.map(m => m.rowId),
      material_id: materials.map(m => m.materialId),
      quantity_issued: materials.map(m => Number(m.quantityIssued)),
      uom: materials.map(m => m.uom),
      unit_cost: materials.map(m => Number(m.unitCost) || 0),
      total_cost: materials.map(m =>
        Number(m.quantityIssued) * Number(m.unitCost || 0)
      ),
      total_transfer_value: totalTransferValue,
    };

    console.log("FINAL PAYLOAD", payload);

    try {
      setSubmitting(true);

      const res = await axios.post(
        `${BASE_URL}/v1/material_requisition/issuance`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data?.status) {
        Swal.fire("Success", "Materials issued successfully!", "success")
          .then(() => {
            onClose();
            if (refreshList && typeof refreshList === 'function') {
              refreshList();
            }
          });
      } else {
        Swal.fire("Error", JSON.stringify(res.data?.message), "error");
      }
    } catch (error) {
      const backendMessage = error.response?.data?.message || "Submission failed";
      Swal.fire("Error", JSON.stringify(backendMessage), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const totalTransferValue = materials.reduce(
    (sum, item) => sum + Number(item.totalValue || 0),
    0
  );

  // Skeleton Loader Components
  const SelectSkeleton = () => (
    <div className={classes.skeletonSelect}>
      <div className={classes.skeletonSelectInner}></div>
    </div>
  );

  const TableSkeleton = () => {
    return Array.from({ length: 3 }).map((_, index) => (
      <tr key={index} className={classes.skeletonRow}>
        <td><div className={classes.skeletonCell}></div></td>
        <td><div className={classes.skeletonCell}></div></td>
        <td><div className={classes.skeletonCell}></div></td>
        <td><div className={classes.skeletonCell}></div></td>
        <td><div className={classes.skeletonCell}></div></td>
        <td><div className={classes.skeletonCell}></div></td>
        <td><div className={classes.skeletonCell}></div></td>
      </tr>
    ));
  };

  if (!isOpen) return null;

  return (
    <div className={classes.overlay}>
      <div className={classes.modal}>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.title}>
            <FileText className={classes.icon} />
            <h2>Materials Issued</h2>
          </div>
          <X className={classes.closeIcon} onClick={onClose} disabled={submitting} />
        </div>

        <p className={classes.subtitle}>
          Record of materials issued to goods warehouse.
        </p>

        <form className={classes.form} onSubmit={handleSubmit}>
          <div className={classes.row}>
            <div className={classes.field}>
              <label>Production Order No</label>
              {loadingOrders ? (
                <SelectSkeleton />
              ) : (
                <select
                  value={selectedProductionOrder}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedProductionOrder(id);
                    if (id) {
                      fetchProductionMaterials(id);
                    } else {
                      setMaterials([]);
                    }
                  }}
                  disabled={submitting}
                >
                  <option value="">
                    -- Select Production Order --
                  </option>
                  {productionOrders.map((po) => (
                    <option key={po.id} value={po.id}>
                      {po.production_number}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className={classes.sectionHeader}>
            <Settings className={classes.sectionIcon} />
            <h3>Material Issued Details</h3>
          </div>

          <table className={classes.table}>
            <thead>
              <tr>
                <th>Materials</th>
                <th>Requisition No</th>
                <th>UOM</th>
                <th>Qty Requested</th>
                <th>Qty Issued</th>
                <th>Unit Cost</th>
                <th>Total Value</th>
              </tr>
            </thead>
            <tbody>
              {loadingMaterials ? (
                <TableSkeleton />
              ) : materials.length === 0 ? (
                <tr>
                  <td colSpan="7" className={classes.noData}>
                    {selectedProductionOrder 
                      ? "No materials found for this production order" 
                      : "Select a production order to view materials"}
                  </td>
                </tr>
              ) : (
                materials.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        value={row.material}
                        readOnly
                        className={classes.readOnlyInput}
                      />
                    </td>
                    <td>
                      <input 
                        value={row.requisitionNo} 
                        readOnly 
                        className={classes.readOnlyInput}
                      />
                    </td>
                    <td>
                      <input 
                        value={row.uom} 
                        readOnly 
                        className={classes.readOnlyInput}
                      />
                    </td>
                    <td>
                      <input 
                        value={row.quantityRequested} 
                        readOnly 
                        className={classes.readOnlyInput}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={row.quantityIssued}
                        onChange={(e) => {
                          const updated = [...materials];
                          const qtyIssued = e.target.value;
                          updated[index].quantityIssued = qtyIssued;
                          if (qtyIssued && updated[index].unitCost) {
                            updated[index].totalValue =
                              Number(qtyIssued) * Number(updated[index].unitCost);
                          } else {
                            updated[index].totalValue = "";
                          }
                          setMaterials(updated);
                        }}
                        disabled={submitting}
                        className={classes.quantityInput}
                      />
                    </td>
                    <td className={classes.rightAlign}>
                      <input
                        type="number"
                        placeholder="Unit cost"
                        value={row.unitCost}
                        onChange={(e) => {
                          const updated = [...materials];
                          const unitCost = e.target.value;
                          updated[index].unitCost = unitCost;
                          if (unitCost && updated[index].quantityIssued) {
                            updated[index].totalValue =
                              Number(unitCost) * Number(updated[index].quantityIssued);
                          } else {
                            updated[index].totalValue = "";
                          }
                          setMaterials(updated);
                        }}
                        disabled={submitting}
                        className={classes.costInput}
                      />
                    </td>
                    <td className={classes.rightAlign}>
                      <input
                        type="number"
                        value={row.totalValue}
                        readOnly
                        className={classes.readOnlyInput}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className={`${classes.row} ${classes.totalRow}`}>
            <div className={classes.totalField}>
              <label>Total Transfer Value:</label>
              <div className={classes.totalValue}>
                {totalTransferValue.toLocaleString()}
              </div>
            </div>
          </div>

          <div className={classes.submitSection}>
            <button
              type="submit"
              className={classes.submitBtn}
              disabled={submitting || materials.length === 0}
            >
              {submitting ? (
                <span className={classes.loadingText}>
                  <span className={classes.spinner}></span>
                  Submitting...
                </span>
              ) : (
                "Complete Transfer"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaterialIssuedModal;